import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { openModal } from "risa-oasis-ui_v2";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { FirestoreService } from "../../../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../../shared/api/firebase/references";
import { SortOption } from "../components/reorderModal/reorderModal";

export interface OptionsConfigParams<T> {
  selectedOrgId: string;
  fetchDocumentRef: (orgId: string) => string;
  documentKey: string;
  modalId: string;
  deleteModalId: string;
  getUniqueKey: (item: T) => string;
  sortFields?: SortOption[];
  reorderModalId?: string;
}

interface BaseOptionModel {
  priority?: number;
  left_pane_display_order?: number;
}

export const useOptionsConfig = <T extends BaseOptionModel>({
  selectedOrgId,
  fetchDocumentRef,
  documentKey,
  modalId,
  deleteModalId,
  getUniqueKey,
  sortFields = ["priority"],
  reorderModalId,
}: OptionsConfigParams<T>) => {
  const [optionsList, setOptionsList] = useState<T[]>([]);
  const [pendingOptionsList, setPendingOptionsList] = useState<T[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editingOption, setEditingOption] = useState<T | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deletingOption, setDeletingOption] = useState<T | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>(sortFields[0]);
  const [showReorderModal, setShowReorderModal] = useState<boolean>(false);

  const hasUnsavedChanges = pendingOptionsList !== null;
  const hasMultipleSortFields = sortFields.length > 1;

  const sortedOptionsList = useMemo(() => {
    const listToSort = pendingOptionsList ?? optionsList;
    return [...listToSort].sort((a, b) => {
      const aValue = a[sortOption as keyof T] as number | undefined;
      const bValue = b[sortOption as keyof T] as number | undefined;

      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;
      return aValue - bValue;
    });
  }, [optionsList, pendingOptionsList, sortOption]);

  const fetchOptions = useCallback(async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    setPendingOptionsList(null);
    try {
      const response = await FirestoreService.getDocument<{ data: T[] }>(
        fetchDocumentRef(selectedOrgId),
      );

      if (response?.data) {
        setOptionsList(response.data);
      } else {
        setOptionsList([]);
      }
    } catch (error) {
      console.error(`Error fetching ${documentKey}:`, error);
      setOptionsList([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, fetchDocumentRef, documentKey]);

  useEffect(() => {
    if (selectedOrgId) {
      fetchOptions();
    }
  }, [selectedOrgId, fetchOptions]);

  useEffect(() => {
    if (showModal) {
      openModal(modalId);
    }
  }, [showModal, modalId]);

  useEffect(() => {
    if (showDeleteModal) {
      openModal(deleteModalId);
    }
  }, [showDeleteModal, deleteModalId]);

  useEffect(() => {
    if (showReorderModal && reorderModalId) {
      openModal(reorderModalId);
    }
  }, [showReorderModal, reorderModalId]);

  const handleAdd = useCallback(() => {
    setModalMode("add");
    setEditingOption(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((option: T) => {
    setModalMode("edit");
    setEditingOption(option);
    setShowModal(true);
  }, []);

  const handleDeleteClick = useCallback((option: T) => {
    setDeletingOption(option);
    setShowDeleteModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditingOption(null);
  }, []);

  const handleSave = useCallback(
    async (option: T) => {
      try {
        const currentList = pendingOptionsList ?? optionsList;
        let updatedList: T[];

        if (modalMode === "add") {
          updatedList = [...currentList, option];
        } else {
          updatedList = currentList.map((o) =>
            getUniqueKey(o) === getUniqueKey(editingOption!) ? option : o,
          );
        }

        await FirestoreService.setDocument(
          FirestoreCollectionReference.facilityOptions(selectedOrgId),
          documentKey,
          { data: updatedList },
        );

        setOptionsList(updatedList);
        setPendingOptionsList(null);
        handleModalClose();
        if (modalMode === "add") {
          toast.success(ToastMessages.OPTION_ADD_SUCCESS);
        } else {
          toast.success(ToastMessages.OPTION_EDIT_SUCCESS);
        }
      } catch (error) {
        console.error(`Error saving ${documentKey}:`, error);
        if (modalMode === "add") {
          toast.error(ToastMessages.OPTION_ADD_ERROR);
        } else {
          toast.error(ToastMessages.OPTION_EDIT_ERROR);
        }
        throw error;
      }
    },
    [
      modalMode,
      optionsList,
      pendingOptionsList,
      editingOption,
      selectedOrgId,
      documentKey,
      getUniqueKey,
      handleModalClose,
    ],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingOption) return;

    try {
      const currentList = pendingOptionsList ?? optionsList;
      const updatedList = currentList.filter(
        (o) => getUniqueKey(o) !== getUniqueKey(deletingOption),
      );

      await FirestoreService.setDocument(
        FirestoreCollectionReference.facilityOptions(selectedOrgId),
        documentKey,
        { data: updatedList },
      );

      setOptionsList(updatedList);
      setPendingOptionsList(null);
      setShowDeleteModal(false);
      setDeletingOption(null);
      toast.success(ToastMessages.OPTION_DELETE_SUCCESS);
    } catch (error) {
      console.error(`Error deleting ${documentKey}:`, error);
      toast.error(ToastMessages.OPTION_DELETE_ERROR);
      throw error;
    }
  }, [
    deletingOption,
    optionsList,
    pendingOptionsList,
    selectedOrgId,
    documentKey,
    getUniqueKey,
  ]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setDeletingOption(null);
  }, []);

  const handleReorderClick = useCallback(() => {
    setShowReorderModal(true);
  }, []);

  const handleReorderModalClose = useCallback(() => {
    setShowReorderModal(false);
  }, []);

  const handleReorderSave = useCallback((newSortOption: SortOption) => {
    setSortOption(newSortOption);
    setShowReorderModal(false);
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === targetIndex) return;

      const newList = [...sortedOptionsList];
      const [draggedItem] = newList.splice(draggedIndex, 1);
      newList.splice(targetIndex, 0, draggedItem);

      const updatedList = newList.map((item, index) => ({
        ...item,
        [sortOption]: index + 1,
      }));

      setPendingOptionsList(updatedList);
      setDraggedIndex(null);
    },
    [draggedIndex, sortedOptionsList, sortOption],
  );

  const handleSaveReorder = useCallback(async () => {
    if (!pendingOptionsList) return;

    setSaving(true);
    try {
      await FirestoreService.setDocument(
        FirestoreCollectionReference.facilityOptions(selectedOrgId),
        documentKey,
        { data: pendingOptionsList },
      );
      setOptionsList(pendingOptionsList);
      setPendingOptionsList(null);
      toast.success(ToastMessages.OPTION_REORDER_SAVE_SUCCESS);
    } catch (error) {
      console.error(`Error saving ${documentKey} order:`, error);
      toast.error(ToastMessages.OPTION_REORDER_SAVE_ERROR);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [pendingOptionsList, selectedOrgId, documentKey]);

  const handleDiscardReorder = useCallback(() => {
    setPendingOptionsList(null);
  }, []);

  const handleCopyFromOrg = useCallback(
    async (sourceOrgId: string) => {
      if (!sourceOrgId || !selectedOrgId) return;

      setLoading(true);
      try {
        // Fetch options from source organization
        const response = await FirestoreService.getDocument<{ data: T[] }>(
          fetchDocumentRef(sourceOrgId),
        );

        if (response?.data && response.data.length > 0) {
          // Save the copied options to the current organization
          await FirestoreService.setDocument(
            FirestoreCollectionReference.facilityOptions(selectedOrgId),
            documentKey,
            { data: response.data },
          );

          setOptionsList(response.data);
          setPendingOptionsList(null);
          toast.success(ToastMessages.OPTION_COPY_SUCCESS);
        }
      } catch (error) {
        console.error(`Error copying ${documentKey} from organization:`, error);
        toast.error(ToastMessages.OPTION_COPY_ERROR);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [selectedOrgId, fetchDocumentRef, documentKey],
  );

  return {
    optionsList,
    sortedOptionsList,
    loading,
    saving,
    hasUnsavedChanges,
    showModal,
    modalMode,
    editingOption,
    showDeleteModal,
    deletingOption,
    draggedIndex,
    sortOption,
    showReorderModal,
    hasMultipleSortFields,
    handleAdd,
    handleEdit,
    handleDeleteClick,
    handleModalClose,
    handleSave,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleReorderClick,
    handleReorderModalClose,
    handleReorderSave,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDrop,
    handleSaveReorder,
    handleDiscardReorder,
    handleCopyFromOrg,
  };
};
